import { useEffect, useState } from "react";
import { api } from "@/lib/api";

interface ResumeTabProps { candidateId: number; hasFile: boolean; mime: string | null }

export function ResumeTab({ candidateId, hasFile, mime }: ResumeTabProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resolvedMime, setResolvedMime] = useState<string | null>(mime);

  useEffect(() => {
    if (!hasFile) return;
    let active = true;
    let createdUrl: string | null = null;
    setLoading(true);
    setError(null);
    api
      .fetchResumeFile(candidateId)
      .then(({ blob, mime }) => {
        if (!active) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
        setResolvedMime(mime);
      })
      .catch((e: Error) => { if (active) setError(e.message || "Failed to load resume"); })
      .finally(() => { if (active) setLoading(false); });
    return () => {
      active = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [candidateId, hasFile]);

  if (!hasFile) {
    return (
      <div className="rounded-card border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        No resume file on file for this candidate. Open the candidate card and use "Re-upload resume" to attach the original PDF; it will be saved to storage and shown here.
      </div>
    );
  }

  if (loading) return <p className="text-sm text-text-secondary">Loading resume…</p>;
  if (error) return <p className="text-sm text-error-text">Failed to load resume: {error}</p>;
  if (!blobUrl) return null;

  const isPdf = (resolvedMime || "").includes("pdf");
  const isImage = (resolvedMime || "").startsWith("image/");

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-end gap-2">
        <a href={blobUrl} target="_blank" rel="noopener noreferrer" className="rounded-btn border border-border-card bg-white px-3 py-1.5 text-xs font-medium text-text-focused transition-colors hover:bg-surface-secondary hover:text-near-black">
          Open in new tab
        </a>
      </div>
      {isPdf ? (
        <iframe src={blobUrl} title="Candidate resume" className="h-[80vh] w-full rounded-card border border-border-card bg-white" />
      ) : isImage ? (
        <img src={blobUrl} alt="Candidate resume" className="max-h-[80vh] w-full rounded-card border border-border-card bg-white object-contain" />
      ) : (
        <p className="text-sm text-text-secondary">
          Resume file type ({resolvedMime || "unknown"}) can't be previewed inline. Use "Open in new tab" to download it.
        </p>
      )}
    </div>
  );
}
